class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initReservationTimer();
    this.initUpsellButtons();
  }

  initReservationTimer() {
    // Clear any existing interval to prevent duplicates
    if (this.reservationInterval) {
      clearInterval(this.reservationInterval);
      this.reservationInterval = null;
    }

    const reservationBar = this.querySelector('.cart-drawer__reservation');
    if (!reservationBar) return;

    const minutes = parseInt(reservationBar.dataset.reservationMinutes) || 5;
    const timerElement = reservationBar.querySelector('.cart-drawer__reservation-timer');
    if (!timerElement) return;

    // Use stored time if available, otherwise start fresh
    if (typeof this.reservationTotalSeconds === 'undefined' || this.reservationTotalSeconds <= 0) {
      this.reservationTotalSeconds = minutes * 60;
    }

    const updateTimer = () => {
      const timerEl = this.querySelector('.cart-drawer__reservation-timer');
      if (!timerEl) return;

      const mins = Math.floor(this.reservationTotalSeconds / 60);
      const secs = this.reservationTotalSeconds % 60;
      timerEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;

      if (this.reservationTotalSeconds <= 0) {
        this.reservationTotalSeconds = minutes * 60;
      } else {
        this.reservationTotalSeconds--;
      }
    };

    // Update immediately, then every second
    updateTimer();
    this.reservationInterval = setInterval(updateTimer, 1000);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.initReservationTimer();
      this.initUpsellButtons();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initUpsellButtons() {
    const upsellButtons = this.querySelectorAll('.cart-drawer-upsell__add');
    upsellButtons.forEach((button) => {
      button.addEventListener('click', this.handleUpsellAdd.bind(this));
    });
  }

  async handleUpsellAdd(event) {
    const button = event.currentTarget;
    const item = button.closest('.cart-drawer-upsell__item');
    const variantId = item?.dataset.variantId;

    if (!variantId || button.classList.contains('loading')) return;

    button.classList.add('loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const data = await response.json();
      this.renderContents(data);
    } catch (error) {
      console.error('Upsell add to cart error:', error);
      button.classList.remove('loading');
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
